import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * AgSUS Organograma — estrutura institucional.
 *
 * Dados hard-coded (estrutura muda raro; quando muda, vira PR no addon).
 * Editor controla apenas o titulo via sidebar.
 *
 * Layout:
 *  - Desktop (>=1024px): governanca em 3 colunas (CTT — CDA — CFA),
 *    DIREX como container tracejado com 3 colunas (DAIS/PRES/DIOP).
 *  - Tablet (768-1023): 3 colunas DAIS/PRES/DIOP empilham.
 *  - Mobile (<768): vira accordion (toca na diretoria, expande unidades).
 */

const ORG = {
  // Governanca em 3 colunas: CTT (sólido) — CDA (centro) — CFA (tracejado)
  technicalChamber: {
    acronym: 'CTT',
    name: 'Câmara Técnica Temática',
    tone: 'sky',
  },
  deliberativeCouncil: {
    acronym: 'CDA',
    name: 'Conselho Deliberativo',
    tone: 'pink',
  },
  fiscalCouncil: {
    acronym: 'CFA',
    name: 'Conselho Fiscal',
    tone: 'sand',
  },
  direxLabel: 'Diretoria Executiva (DIREX)',
  directorates: [
    {
      id: 'dais',
      acronym: 'DAIS',
      name: 'Diretoria de Atenção Integral à Saúde',
      tone: 'green',
      units: [
        { acronym: 'UMA', name: 'Monitoramento e Avaliação' },
        { acronym: 'UDIGIS', name: 'Transformação Digital em Saúde' },
        { acronym: 'UAPS', name: 'Atenção Primária à Saúde' },
        { acronym: 'UEP', name: 'Ensino e Pesquisa' },
        { acronym: 'UPI', name: 'Projetos e Inovação' },
        { acronym: 'UAE', name: 'Atenção Especializada' },
        {
          acronym: 'USI',
          name: 'Saúde Indígena',
          nucleus: {
            acronym: 'NEPSI',
            name: 'Núcleo Especializado Provimento Saúde Indígena',
          },
        },
      ],
    },
    {
      id: 'pres',
      acronym: 'PRES',
      name: 'Presidência',
      tone: 'purple',
      // Tudo empilhado verticalmente igual DAIS/DIOP. Nucleos (NAA, NIT)
      // ficam logo abaixo de suas unidades (GAB e SEAR respectivamente),
      // mesma estetica do NEPSI ↔ USI.
      units: [
        {
          acronym: 'GAB',
          name: 'Gabinete',
          nucleus: { acronym: 'NAA', name: 'Núcleo AgSUS Acolhedora' },
        },
        { acronym: 'UINT', name: 'Integridade' },
        { acronym: 'UCOM', name: 'Comunicação' },
        {
          acronym: 'SEAR',
          name: 'Secretaria Executiva e de Articulação',
          nucleus: {
            acronym: 'NIT',
            name: 'Núcleo de Inovação Tecnológica',
          },
        },
        { acronym: 'UJUR', name: 'Jurídico' },
        {
          acronym: 'UFOC',
          name: 'Finanças, Orçamento, Contabilidade e Prestação de Contas',
        },
        { acronym: 'UPGE', name: 'Planejamento e Gestão Estratégica' },
      ],
    },
    {
      id: 'diop',
      acronym: 'DIOP',
      name: 'Diretoria de Operações',
      tone: 'orange',
      units: [
        { acronym: 'UGP', name: 'Gestão de Pessoas' },
        { acronym: 'UAC', name: 'Aquisições e Contratos' },
        { acronym: 'ULOG', name: 'Logística, Suprimentos e Patrimônio' },
        {
          acronym: 'UTIC',
          name: 'Tecnologia da Informação e Comunicação',
        },
        { acronym: 'UINFRA', name: 'Infraestrutura' },
        { acronym: 'UEV', name: 'Eventos' },
      ],
    },
  ],
};

const Box = ({ acronym, name, tone, size = 'md', className = '' }) => (
  <div
    className={`ag-org__box ag-org__box--${size} ${className}`.trim()}
    data-tone={tone}
  >
    <span className="ag-org__box-name">{name}</span>
    {acronym && <span className="ag-org__box-acronym">({acronym})</span>}
  </div>
);
Box.propTypes = {
  acronym: PropTypes.string,
  name: PropTypes.string.isRequired,
  tone: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
};

const Unit = ({ unit, tone }) => (
  <li className="ag-org__unit">
    <div className="ag-org__unit-box" data-tone={tone}>
      <span className="ag-org__box-name">{unit.name}</span>
      <span className="ag-org__box-acronym">({unit.acronym})</span>
    </div>
    {unit.nucleus && (
      <div className="ag-org__nucleus" data-tone="neutral">
        <span className="ag-org__nucleus-name">{unit.nucleus.name}</span>
        {unit.nucleus.acronym && (
          <span className="ag-org__nucleus-acronym">
            {' '}({unit.nucleus.acronym})
          </span>
        )}
      </div>
    )}
  </li>
);
Unit.propTypes = {
  unit: PropTypes.object.isRequired,
  tone: PropTypes.string,
};

const Directorate = ({ dir, open, onToggle }) => {
  const { id, acronym, name, tone, units } = dir;
  return (
    <article
      className={`ag-org__directorate ag-org__directorate--${id}${open ? ' is-open' : ''}`}
      data-tone={tone}
    >
      <button
        type="button"
        className="ag-org__directorate-head"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`ag-org-body-${id}`}
        data-tone={tone}
      >
        <span className="ag-org__box-name">{name}</span>
        <span className="ag-org__box-acronym">({acronym})</span>
        <span className="ag-org__chevron" aria-hidden="true" />
      </button>

      <div id={`ag-org-body-${id}`} className="ag-org__directorate-body">
        <ol className="ag-org__units">
          {units.map((u) => (
            <Unit key={u.acronym} unit={u} tone={tone} />
          ))}
        </ol>
      </div>
    </article>
  );
};
Directorate.propTypes = {
  dir: PropTypes.object.isRequired,
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

const OrganogramaView = ({ data }) => {
  const title = data?.title || 'Organograma da AgSUS';
  const [openId, setOpenId] = useState(null);

  const handleToggle = (id) => {
    setOpenId((cur) => (cur === id ? null : id));
  };

  return (
    <section className="ag-org" aria-label="Organograma institucional">
      {title && <h2 className="ag-org__title">{title}</h2>}

      {/* CTT — CDA — CFA, ligados em uma linha horizontal */}
      <div className="ag-org__governance" role="group" aria-label="Governança">
        <Box
          {...ORG.technicalChamber}
          size="md"
          className="ag-org__gov ag-org__gov--chamber"
        />
        <Box
          {...ORG.deliberativeCouncil}
          size="lg"
          className="ag-org__gov ag-org__gov--cda"
        />
        <Box
          {...ORG.fiscalCouncil}
          size="md"
          className="ag-org__gov ag-org__gov--cfa"
        />
      </div>

      <div className="ag-org__cda-to-direx" aria-hidden="true" />

      <div className="ag-org__direx">
        <div className="ag-org__direx-label">{ORG.direxLabel}</div>

        <div className="ag-org__directorates">
          {ORG.directorates.map((d) => (
            <Directorate
              key={d.id}
              dir={d}
              open={openId === d.id}
              onToggle={() => handleToggle(d.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

OrganogramaView.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
  }).isRequired,
};

export default OrganogramaView;
